import { Link } from "react-router-dom";
import { ArrowRight, Shield, Award, Wrench, Leaf, FileText } from "lucide-react";
import { HERO_IMG, PATIO_IMG, ACCESSORY_IMG, TEXTURE_IMG, DATASHEET_URL } from "../lib/constants";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[92vh] min-h-[600px] overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-no-repeat bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative h-full max-w-7xl mx-auto px-6 sm:px-8 flex flex-col justify-end pb-24">
          <div className="max-w-2xl reveal">
            <div className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-6">
              <span className="gold-rule" />Prémium alumínium pergola
            </div>
            <h1 className="font-serif text-5xl sm:text-7xl text-white leading-[1.05] font-light">
              A nyári napok<br />új arculata.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/90 max-w-xl leading-relaxed">
              A SolarSimple pergola elegáns formavilágot, mérnöki precizitást és
              komfortot ötvöz – kifejezetten az Ön igényeihez igazítva. Rendelje meg most!
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/termek"
                data-testid="hero-cta-button"
                className="inline-flex items-center gap-3 bg-[#d4af37] text-white px-8 py-4 uppercase tracking-[0.2em] text-xs hover:bg-[#aa8b2b] transition-colors"
              >
                Konfigurálom <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/funkciok"
                data-testid="hero-secondary-button"
                className="inline-flex items-center gap-3 border-2 border-white text-white px-8 py-4 uppercase tracking-[0.2em] text-xs hover:bg-white hover:text-[#005f6b] transition-colors"
              >
                Tudjon meg többet
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 right-8 hidden md:flex items-center gap-3 text-white/80 text-xs uppercase tracking-[0.3em]">
          <span className="h-px w-12 bg-[#d4af37]" />
          Made by Tarasola
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 sm:py-32 bg-[#03161c]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-4">
              <span className="gold-rule" />Filozófia
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl text-white leading-tight">
              Időtálló elegancia, kompromisszumok nélkül.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 space-y-6">
            <p className="text-lg text-white/90 leading-relaxed font-light">
              Minden SolarSimple pergola egy elegáns kifejezése annak, hogy az otthonod is megérdemli az igényes formát.
            </p>
            <p className="text-base text-white/70 leading-relaxed">
              Prémium alumínium, motoros vagy kézi vezérlés, hangtalan
              működés és időjárás-érzékelők – a komfortot mi tervezzük,
              Önnek csak élvezni kell.
            </p>
          </div>
        </div>
      </section>

      {/* FEATURE GRID */}
      <section className="py-24 sm:py-32 bg-[#031a20]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="grid md:grid-cols-2 gap-1">
            <div
              className="relative aspect-[4/5] bg-contain bg-no-repeat bg-center bg-[#04222a]"
              style={{ backgroundImage: `url(${PATIO_IMG})` }}
              data-testid="features-image"
            />
            <div className="bg-[#04222a] p-12 flex flex-col justify-center">
              <div className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-4">
                <span className="gold-rule" />Mérnöki precizitás
              </div>
              <h3 className="font-serif text-4xl text-white leading-tight">
                Akár 4,5 méter szélességig, 4,5 méter kinyúlással.
              </h3>
              <p className="mt-6 text-base text-white/70 leading-relaxed">
                A SolarSimple pergola rendszer méreteit az otthonához igazítjuk.
                Az erős vázszerkezet és a precíz Opatex vászonkezelés tartós
                használatot biztosít minden évszakban.
              </p>
              <div className="mt-10 grid grid-cols-2 gap-6">
                <Stat label="Szerkezeti Garancia" value="5 év" />
                <Stat label="Szélállóság" value="50 km/h" />
                <Stat label="Vízállóság" value="Teljesen vízhatlan" />
                <Stat label="Telepítés" value="4 óra" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* USP icons */}
      <section className="py-24 bg-[#03161c]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { id: "garantia", i: Shield, t: "5 év szerkezeti garancia", d: "Gondtalan tulajdonlás évekre." },
            { id: "premium", i: Award, t: "Prémium minőség", d: "Eloxált alumínium szerkezet." },
            { id: "szereles", i: Wrench, t: "Egyedi szerelés", d: "Saját kivitelező csapat." },
            { id: "energia", i: Leaf, t: "Energiatakarékos", d: "LED világítás, alacsony fogyasztás." },
          ].map((b) => (
            <div key={b.id} className="bg-[#04222a] border border-white/10 p-8">
              <b.i className="w-7 h-7 text-[#d4af37] mb-6" strokeWidth={1.5} />
              <div className="font-serif text-xl text-white">{b.t}</div>
              <p className="text-sm text-white/70 mt-2 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ACCESSORIES */}
      <section className="py-24 sm:py-32 bg-[#031a20]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-4">
              <span className="gold-rule" />Okos kiegészítők
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl text-white leading-tight">
              Csendes vezérlés egy érintéssel.
            </h2>
            <p className="mt-6 text-base text-white/70 leading-relaxed max-w-xl">
              Az alkalmazás teljes irányítást ad. Integrált LED
              világítás, automata szél- és esőérzékelők, mind
              a saját ízlése szerint hangolva.
            </p>
            <ul className="mt-8 grid grid-cols-2 gap-3 text-sm text-white">
              {["LED Spot", "LED Box", "Szélérzékelő", "SOMFY motor", "Okostelefon vezérlés"].map((x) => (
                <li key={x} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#d4af37]" />{x}
                </li>
              ))}
            </ul>
          </div>
          <div
            className="lg:col-span-5 aspect-square bg-cover bg-no-repeat bg-center order-1 lg:order-2"
            style={{ backgroundImage: `url(${ACCESSORY_IMG})` }}
          />
        </div>
      </section>

      {/* DATASHEET */}
      <section className="py-20 bg-[#03161c] border-y border-[#d4af37]/10" data-testid="datasheet-section">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-4">
              <span className="gold-rule" />Termékadatlap
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl text-white leading-tight">
              Töltse le a SolarSimple pergola hivatalos termékadatlapját.
            </h2>
            <p className="mt-4 text-base text-white/70 leading-relaxed max-w-xl">
              Minden technikai részlet, méret, anyag és garancia információ egyetlen letölthető PDF-ben.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <a
              href={DATASHEET_URL}
              target="_blank"
              rel="noopener noreferrer"
              download
              data-testid="home-datasheet-link"
              className="inline-flex items-center gap-3 bg-[#d4af37] text-[#031a20] hover:bg-[#e5c865] px-8 py-4 uppercase tracking-[0.2em] text-xs transition-colors font-medium"
            >
              <FileText className="w-5 h-5" /> Letöltés (PDF)
            </a>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section
        className="relative py-32 bg-cover bg-no-repeat bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0,72,82,0.85), rgba(0,72,82,0.85)), url(${TEXTURE_IMG})`,
        }}
      >
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-6">
            <span className="h-px inline-block w-12 bg-[#d4af37] align-middle mr-3" />
            Készen áll a következő lépésre?
          </div>
          <h2 className="font-serif text-4xl sm:text-6xl text-white leading-tight">
            Tervezze meg pergoláját<br />még ma.
          </h2>
          <Link
            to="/termek"
            data-testid="cta-banner-button"
            className="mt-12 inline-flex items-center gap-3 bg-[#d4af37] text-white px-10 py-5 uppercase tracking-[0.2em] text-xs hover:bg-[#aa8b2b] transition-colors"
          >
            Indítom a konfigurátort <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-white/60">{label}</div>
      <div className="font-serif text-3xl text-[#d4af37] mt-1">{value}</div>
    </div>
  );
}
